import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  IconButton,
  CardMedia,
  Chip,
  Dialog,
  DialogTitle,
  DialogActions,
  Menu,
  MenuItem,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Autocomplete,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Pagination,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CALCULATOR_API_URL,
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_RED,
  exchangeRates,
  FREE_REPORT_CREATION_LIMIT,
} from 'src/utils/const';
import {
  SearchAddressDetailsType,
  handleLoginOrPricingModalOpenType,
} from '../../propTypes';
import SearchAutoComplete from './Components/SearchAutoComplete';
import { User } from 'src/interfaces/iuser';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  checkIfSettingPage,
  checkIfUserLoggedIn,
  convertCurrency,
  formatCurrency,
  getMapboxStaticPropertyPreviewUrl,
  getUserPreferredCurrency,
  sortUserMarkets,
  formatDate,
} from 'src/utils/functions';
import { MAPBOX_ACCESS_TOKEN } from 'src/utils/keys';
import { callApi } from 'src/core/callApi';
import ShareLinkModal from 'src/App/Dashboards/Market/Components/Modals/ShareLinkModal';
import SortTypeSelectionMenu from 'src/App/Dashboards/Market/Components/SortTypeSelectionMenu';
import { logAmplitudeEvent } from 'src/utils/analytics';
import bookingCom from 'src/App/assets/booking_com.jpg';
import { COLOR_PRIMARY_2 } from 'src/utils/colors';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbarContainer,
  GridToolbarExport,
} from '@mui/x-data-grid';
import RateLimitAlert from 'src/App/Dashboards/Market/Components/Alerts/RateLimitAlert';
const TAG_FILTER_ALL = '__all__';
const TAG_FILTER_UNTAGGED = '__untagged__';

type EstimatorReportTagFilterItem = {
  id: string;
  name: string;
  isDefault?: boolean;
  count?: number;
};

const ESTIMATOR_SAVED_REPORTS_VIEW_KEY = 'estimator-saved-reports-view';
const SAVED_REPORT_CARDS_PER_PAGE = 6;
/** Debounce address filter to avoid churning visible cards (and Mapbox static image requests) on every keystroke. */
const SAVED_REPORTS_ADDRESS_SEARCH_DEBOUNCE_MS = 300;
const FALLBACK_SAVED_REPORT_CARD_MAP_IMAGE =
  'https://airbtics-wordpress-market-assets.s3.us-east-2.amazonaws.com/map-image-no-label-demo.png';
type SavedReportsViewMode = 'cards' | 'table';

const savedReportsTagChipLabel = (item: EstimatorReportTagFilterItem) => {
  if (item.isDefault) return item.name;
  if (item.count != null) return `${item.name} (${item.count})`;
  return item.name;
};

const getInitialSavedReportsViewMode = (): SavedReportsViewMode => {
  try {
    const v = localStorage.getItem(ESTIMATOR_SAVED_REPORTS_VIEW_KEY);
    if (v === 'table' || v === 'cards') return v;
  } catch {
    /* ignore */
  }
  return 'cards';
};

const escapeCsvCell = (value: string) => {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const downloadSavedReportsCsv = (rows: any[], currency: string) => {
  const header = [
    'Address',
    'Report Type',
    'Created On',
    'Revenue',
    'Monthly Profit',
    'Annual Return Rate %',
  ];
  const lines = [header.join(',')];
  for (const r of rows) {
    const revenue = r.ltm_revenue
      ? exchangeRates[currency].symbol +
        formatCurrency(String(convertCurrency(currency, r.ltm_revenue as number)))
      : '_';
    const monthlyProfit = r.monthly_profit
      ? exchangeRates[currency].symbol +
        formatCurrency(String(convertCurrency(currency, r.monthly_profit as number)))
      : '_';
    const arr = r.annual_return_rate != null ? String(r.annual_return_rate) : '_';
    lines.push(
      [
        escapeCsvCell(r.address ?? ''),
        escapeCsvCell(String(r.report_type ?? '')),
        escapeCsvCell(formatDate(r.created_at)),
        escapeCsvCell(revenue),
        escapeCsvCell(monthlyProfit),
        escapeCsvCell(arr),
      ].join(',')
    );
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'calculator-reports.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const styles = {
  root: (matches: boolean, searchbarOnly: boolean = false) => ({
    paddingBottom: searchbarOnly ? '0px' : matches ? '270px' : '80px',
    background: matches ? '#E7F2F4' : '#f4fafb',
  }),
  searchBarContainer: (matches: boolean, searchbarOnly: boolean) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    ...(searchbarOnly
      ? { padding: 0 }
      : matches
      ? { padding: '20px 16px', minHeight: 'unset' }
      : {
          padding: '48px 32px 36px',
          minHeight: 'unset',
        }),
  }),
  searchBarRoot: (matches: boolean) => ({
    padding: matches ? '5px' : '10px',
    width: '100%',
    maxWidth: matches ? '1600px' : '960px',
    minHeight: matches ? '90px' : '92px',
    height: matches ? '90px' : 'auto',
    borderRadius: matches ? '12px' : '16px',
    background: '#E7F2F4',
    boxShadow: matches ? 'none' : '0 8px 30px rgba(3, 90, 105, 0.08)',
    border: matches ? 'none' : '1px solid rgba(3, 122, 144, 0.1)',
  }),
  searchBarSunSectionContainer: (matches: boolean) => ({
    position: 'relative',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    background: '#fff',
    borderRadius: matches ? '12px' : '12px',
    height: '100%',
    ...(matches
      ? { border: '6px solid #E7F2F4' }
      : {
          border: '1px solid rgba(3, 122, 144, 0.14)',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
        }),
  }),
  searchInputLabel: (matches: boolean) => ({
    position: 'absolute',
    top: matches ? '5px' : '8px',
    left: '16px',
    fontSize: matches ? '14px' : '12px',
    color: '#64748b',
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'uppercase' as const,
  }),
  bedroomCounterContainer: {
    marginLeft: '16px',
    marginTop: '20px',
  },
  bedroomCounterButton: (matches: boolean) => ({
    border: matches ? '1px solid #000' : '1px solid #cbd5e1',
    height: '24px',
    width: '24px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
    '&:hover': {
      backgroundColor: matches ? undefined : 'rgba(3, 122, 144, 0.06)',
      borderColor: matches ? undefined : COLOR_PRIMARY,
    },
  }),
  calculateButton: (matches: boolean) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    color: '#fff',
    background: COLOR_PRIMARY_DARK,
    fontSize: '18px',
    fontWeight: 600,
    borderRadius: matches ? 0 : '10px',
    boxShadow: matches ? 'none' : '0 2px 8px rgba(2, 107, 125, 0.35)',
    '&:hover': {
      background: COLOR_PRIMARY,
    },
  }),
};

const SearchBar = ({
  user,
  matches,
  dummyMarket,
  googleAutocompleteSessionToken,
  getGoogleAutocompleteSessionToken,
  clearGoogleAutocompleteSessionToken,
  googleAutocompleteSessionTokenTimeStamp,
  handleReport,
  handleLoginOrPricingModalOpen,
  userReportCreationCount,
  searchbarOnly,
  ota = 'airbnb',
}: {
  user: User;
  matches: boolean;
  dummyMarket: any;
  googleAutocompleteSessionToken: any;
  getGoogleAutocompleteSessionToken: any;
  clearGoogleAutocompleteSessionToken: any;
  googleAutocompleteSessionTokenTimeStamp: any;
  handleReport: (method: any, requestBody: null | any, id?: string) => Promise<{ success: boolean }>;
  handleLoginOrPricingModalOpen: handleLoginOrPricingModalOpenType;
  userReportCreationCount: number;
  searchbarOnly: boolean;
  ota?: 'airbnb' | 'booking';
}) => {
  const isUserLoggedIn: boolean = checkIfUserLoggedIn(user.email);
  const isUserVerified: boolean = user.isVerified;

  const [addressDetails, setAddressDetails] = useState<SearchAddressDetailsType | null>(null);

  const [bedroomCount, setBedroomCount] = useState<number>(1);

  const searchBarInputGridSizeObject = matches
    ? { autoComplete: 12, bedroomCounter: 12, calculateButton: 12 }
    : { autoComplete: 7, bedroomCounter: 3, calculateButton: 2 };

  const handleBedroomCount = (type: 'increase' | 'decrease') => {
    if (type === 'increase') {
      setBedroomCount(prev => Math.min(prev + 1, 6));
    }
    if (type === 'decrease') {
      setBedroomCount(prev => Math.max(prev - 1, 0));
    }
  };

  const handleOnCalculateClick = () => {
    if (addressDetails === null) {
      return;
    }

    if (searchbarOnly) {
      const reDirectURL = `/${
        ota === 'airbnb' ? 'airbnb' : 'booking-com'
      }-calculator?address-full=${addressDetails.address_full}&address-short=${
        addressDetails.address_short
      }&bedrooms=${bedroomCount}&report-type=manage`;
      window.open(reDirectURL, '_self');
      return;
    }

    const ApiRequestObject: { [key: string]: string | number | null } = {
      address_full: addressDetails.address_full,
      address_short: addressDetails.address_short,
      latitude: addressDetails.latitude,
      longitude: addressDetails.longitude,
      bedrooms: bedroomCount,
      report_type: 'manage',
    };

    if (!isUserLoggedIn) {
      handleLoginOrPricingModalOpen('open');
      localStorage.setItem('estimator-create-report', JSON.stringify(ApiRequestObject));
      return;
    }

    if (!isUserVerified) {
      handleLoginOrPricingModalOpen('open', 'emailVerify');
      return;
    }

    if (
      user.subscriptionPlan == 'starter' &&
      userReportCreationCount > FREE_REPORT_CREATION_LIMIT
    ) {
      handleLoginOrPricingModalOpen('open', 'payment');
      return;
    }

    handleReport('CREATE', ApiRequestObject);
  };

  return (
    <Grid
      container
      item
      sx={{
        ...styles.searchBarContainer(matches, searchbarOnly),
        ...(searchbarOnly && {
          paddingLeft: 0,
          paddingRight: 0,
          paddingBottom: 0,
        }),
      }}
      xs={12}
    >
      <Grid container item justifyContent="center" xs={12}>
        <Grid item container sx={styles.searchBarRoot(matches)}>
          <Grid
            item
            xs={searchBarInputGridSizeObject.autoComplete}
            sx={styles.searchBarSunSectionContainer(matches)}
          >
            <Typography variant="body1" sx={styles.searchInputLabel(matches)}>
              Address
            </Typography>
            <SearchAutoComplete
              user={user}
              matches={matches}
              dummyMarket={dummyMarket}
              googleAutocompleteSessionToken={googleAutocompleteSessionToken}
              getGoogleAutocompleteSessionToken={getGoogleAutocompleteSessionToken}
              clearGoogleAutocompleteSessionToken={clearGoogleAutocompleteSessionToken}
              googleAutocompleteSessionTokenTimeStamp={googleAutocompleteSessionTokenTimeStamp}
              setAddressDetails={setAddressDetails}
            />
          </Grid>

          <Grid
            item
            xs={searchBarInputGridSizeObject.bedroomCounter}
            sx={styles.searchBarSunSectionContainer(matches)}
          >
            <Typography variant="body1" sx={styles.searchInputLabel(matches)}>
              Bedrooms
            </Typography>

            <Grid container alignItems="center" sx={styles.bedroomCounterContainer}>
              <Grid
                item
                sx={styles.bedroomCounterButton(matches)}
                onClick={() => handleBedroomCount('decrease')}
              >
                <RemoveRoundedIcon />
              </Grid>
              <Typography
                variant="body1"
                sx={{ fontSize: '16px', marginLeft: '10px', marginRight: '10px' }}
              >
                {bedroomCount === 0 ? 'Studio' : bedroomCount === 6 ? '6+' : bedroomCount}
              </Typography>
              <Grid
                item
                sx={styles.bedroomCounterButton(matches)}
                onClick={() => handleBedroomCount('increase')}
              >
                <AddRoundedIcon />
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={searchBarInputGridSizeObject.calculateButton}
            sx={styles.searchBarSunSectionContainer(matches)}
          >
            <Button
              sx={styles.calculateButton(matches)}
              disabled={addressDetails === null ? true : false}
              onClick={handleOnCalculateClick}
            >
              Calculate
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const getCurrentSavedReportDisplayKpi = (report, currency: string) => {
  const reportType = report.report_type;

  switch (reportType) {
    case 'buy':
      const annualRateOfReturn = report.annual_return_rate;
      return ['Annual Rate of Return', annualRateOfReturn, false];
    case 'rent':
      const monthlyProfit = convertCurrency(currency, report.monthly_profit);
      return ['Monthly Profit', monthlyProfit, true];
    case 'manage':
      const yearlyRevenue = convertCurrency(currency, report.ltm_revenue);
      return ['Yearly Revenue', yearlyRevenue, true];
    default:
      return ['', '', false];
  }
};

const MenuShareDelete = ({
  urlPath,
  currentReportCardId,
  isOptionsOpen,
  setIsOptionsOpen,
  setComingSoonModalOpen,
  handleDeleteReport,
}) => {
  const menuItemStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
  };

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isShareLinkModalOpen, setIsShareLinkModalOpen] = useState<boolean>(false);

  return (
    <>
      <Menu
        id="lock-menu"
        anchorEl={document.getElementById(currentReportCardId)}
        open={isOptionsOpen}
        onClose={() => setIsOptionsOpen(false)}
      >
        <MenuItem
          onClick={() => {
            setIsShareLinkModalOpen(true);
            logAmplitudeEvent('report_shared', { source: 'report_card' });
          }}
        >
          <Box sx={menuItemStyle}>
            <ReplyRoundedIcon sx={{ transform: 'scaleX(-1)' }} />
            Share
          </Box>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setIsDeleteDialogOpen(true);
          }}
        >
          <Box sx={menuItemStyle}>
            <DeleteOutlineRoundedIcon /> Delete
          </Box>
        </MenuItem>
      </Menu>

      <Dialog open={isDeleteDialogOpen}>
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to delete this report?
        </DialogTitle>
        <DialogActions sx={{ padding: '20px' }}>
          <Button
            variant="outlined"
            color="info"
            startIcon={<CloseRoundedIcon />}
            onClick={() => setIsDeleteDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={() => {
              setIsDeleteDialogOpen(false);
              handleDeleteReport();
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <ShareLinkModal
        open={isShareLinkModalOpen}
        setOpen={setIsShareLinkModalOpen}
        shareUrl={`${window.location.origin}/${urlPath}`}
      />
    </>
  );
};

const TAG_NAME_SUGGESTION_LIMIT = 10;

const SavedReportCard = ({
  report,
  currency,
  handleReport,
  setComingSoonModalOpen,
  onlyRenderSavedReports,
  ota,
  onTagsChanged,
  existingUserTagNames,
}: {
  report: any;
  currency: string;
  handleReport: (
    method: any,
    requestBody: null | any,
    id?: string,
    ...args: any[]
  ) => Promise<{ success: boolean }>;
  setComingSoonModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onlyRenderSavedReports: boolean;
  ota: 'airbnb' | 'booking';
  onTagsChanged: () => Promise<void>;
  existingUserTagNames: string[];
}) => {
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [newTagInput, setNewTagInput] = useState('');
  const [tagError, setTagError] = useState('');
  const [tagBusy, setTagBusy] = useState(false);

  const tags: string[] = Array.isArray(report.tags) ? report.tags : [];

  const reportTagsLower = useMemo(
    () => new Set(tags.map(t => String(t).toLowerCase())),
    [tags]
  );

  const tagNameSuggestions = useMemo(() => {
    const q = newTagInput.trim().toLowerCase();
    if (!q) return [];
    return existingUserTagNames
      .filter(
        name =>
          !reportTagsLower.has(name.toLowerCase()) && name.toLowerCase().includes(q)
      )
      .slice(0, TAG_NAME_SUGGESTION_LIMIT);
  }, [newTagInput, existingUserTagNames, reportTagsLower]);

  const cardPreviewMapUrl = useMemo(() => {
    if (!MAPBOX_ACCESS_TOKEN) return null;
    const lat =
      report.latitude != null && report.latitude !== ''
        ? Number(report.latitude)
        : NaN;
    const lng =
      report.longitude != null && report.longitude !== ''
        ? Number(report.longitude)
        : NaN;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return getMapboxStaticPropertyPreviewUrl(lat, lng, MAPBOX_ACCESS_TOKEN, {
      width: 240,
      height: 240,
      zoom: 13,
    });
  }, [report.latitude, report.longitude]);

  const currentReportCardId = 'report-card-' + report.id;
  const urlPath = report.url_path;

  const handleSavedReportClick = () => {
    if (onlyRenderSavedReports) {
      window.open('/' + urlPath);
    } else {
      handleReport('READ', null, report.id);
    }
  };

  const handleDeleteReport = async () => {
    handleReport('DELETE', null, report.id);
    setIsOptionsOpen(false);
  };

  const handleOptionClick = event => {
    event.stopPropagation();
    setIsOptionsOpen(true);
  };

  const [displayKpiName, displayKpiValue, isValueCurrency] = getCurrentSavedReportDisplayKpi(
    report,
    currency
  );

  const address: string = report.address;

  const putReportTags = async (action: 'add' | 'remove' | 'set', tagList: string[]) => {
    setTagBusy(true);
    setTagError('');
    try {
      const data = await callApi(
        CALCULATOR_API_URL,
        {
          body: {
            update_type: 'report_tags',
            id: report.id,
            action,
            tags: tagList,
            ota,
          },
          queryParams: { apiType: 'v3' },
        },
        true,
        false,
        '',
        'PUT',
        true,
        undefined,
        true,
        true
      );
      if (data?.error) {
        setTagError(typeof data.error === 'string' ? data.error : 'Could not update tags');
        return;
      }
      setTagDialogOpen(false);
      setNewTagInput('');
      await onTagsChanged();
    } catch {
      setTagError('Could not update tags');
    } finally {
      setTagBusy(false);
    }
  };

  const handleAddTagSubmit = () => {
    const t = newTagInput.trim();
    if (!t) {
      setTagError('Tag name is required');
      return;
    }
    putReportTags('add', [t]);
  };

  return (
    <>
      <Card
        sx={{
          position: 'relative',
          display: 'flex',
          minWidth: '300px',
          maxWidth: '400px',
          width: '100%',
          cursor: 'pointer',
        }}
        onClick={handleSavedReportClick}
      >
        <CardMedia
          component="img"
          sx={{ width: '30%', objectFit: 'cover', alignSelf: 'stretch', minHeight: 120 }}
          image={cardPreviewMapUrl ?? FALLBACK_SAVED_REPORT_CARD_MAP_IMAGE}
          alt=""
        />
        <Chip
          label={report.report_type}
          sx={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            background: '#fff',
            border: '1px solid ' + COLOR_PRIMARY_DARK,
            color: COLOR_PRIMARY_DARK,
            fontWeight: 'bold',
            textTransform: 'capitalize',
          }}
        />
        <Button
          onClick={handleOptionClick}
          id={currentReportCardId}
          size="small"
          sx={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            background: '#fff',
            color: COLOR_PRIMARY_DARK,
            width: '20px',
            maxWidth: '20px',
            minWidth: '20px',
          }}
        >
          <MoreVertIcon />
        </Button>

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          <CardContent sx={{ pb: 0.5, pt: 1.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mb: 0.5 }}>
              {tags.slice(0, 4).map(t => (
                <Chip
                  key={t}
                  label={t}
                  size="small"
                  onDelete={e => {
                    e.stopPropagation();
                    putReportTags('remove', [t]);
                  }}
                  sx={{ maxWidth: 120 }}
                />
              ))}
              {tags.length > 4 ? (
                <Chip label={`+${tags.length - 4}`} size="small" variant="outlined" />
              ) : null}
              <IconButton
                size="small"
                aria-label="Add tag"
                onClick={e => {
                  e.stopPropagation();
                  setTagDialogOpen(true);
                  setTagError('');
                  setNewTagInput('');
                }}
                sx={{ ml: 0.5 }}
              >
                <LabelOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography component="div" variant="subtitle1">
              {displayKpiName}
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              {isValueCurrency && exchangeRates[currency].symbol}
              {isValueCurrency ? formatCurrency(String(displayKpiValue)) : displayKpiValue}
              {!isValueCurrency && ' %'}
            </Typography>
          </CardContent>
          <CardContent sx={{ paddingBottom: '10 !important' }}>
            <Typography variant="subtitle1" sx={{ alignSelf: 'flex-end', fontSize: '14px' }}>
              {address.slice(0, 25)}
              {address.length > 25 && '...'}
            </Typography>
          </CardContent>
        </Box>
      </Card>

      <MenuShareDelete
        urlPath={urlPath}
        currentReportCardId={currentReportCardId}
        isOptionsOpen={isOptionsOpen}
        setIsOptionsOpen={setIsOptionsOpen}
        setComingSoonModalOpen={setComingSoonModalOpen}
        handleDeleteReport={handleDeleteReport}
      />

      <Dialog open={tagDialogOpen} onClose={() => !tagBusy && setTagDialogOpen(false)}>
        <DialogTitle>Add tag</DialogTitle>
        <Box sx={{ px: 3, pb: 1 }}>
          <Autocomplete
            freeSolo
            options={tagNameSuggestions}
            filterOptions={opts => opts}
            inputValue={newTagInput}
            onInputChange={(_, value) => {
              setNewTagInput(value);
              if (tagError) setTagError('');
            }}
            renderInput={params => (
              <TextField
                {...params}
                label="Tag name"
                fullWidth
                size="small"
                autoFocus
                error={!!tagError}
                helperText={tagError}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleAddTagSubmit();
                }}
              />
            )}
          />
        </Box>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTagDialogOpen(false)} disabled={tagBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddTagSubmit} disabled={tagBusy}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CustomToolbar = () => {
  return (
    <GridToolbarContainer sx={{ justifyContent: 'flex-end', p: 2 }}>
      <GridToolbarExport
        csvOptions={{ utf8WithBom: true, fileName: 'calculator-reports' }}
        printOptions={{ disableToolbarButton: true }}
        size="large"
        variant="outlined"
      />
    </GridToolbarContainer>
  );
};

const SavedReports = ({
  isLoading,
  reports,
  currency,
  handleReport,
  setComingSoonModalOpen,
  matches,
  ota,
  refreshSavedReports,
  isTagFilterActive,
  tagFilterItems,
  selectedTagFilter,
  onTagFilterChange,
  existingUserTagNames,
}: {
  isLoading: boolean;
  reports: any[] | null;
  currency: string;
  handleReport: (
    method: any,
    requestBody: null | any,
    id?: string,
    ...args: any[]
  ) => Promise<{ success: boolean }>;
  setComingSoonModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  matches: boolean;
  ota: 'airbnb' | 'booking';
  refreshSavedReports: () => Promise<void>;
  isTagFilterActive: boolean;
  tagFilterItems: EstimatorReportTagFilterItem[];
  selectedTagFilter: string;
  onTagFilterChange: (id: string) => void;
  existingUserTagNames: string[];
}) => {
  const onlyRenderSavedReports = checkIfSettingPage();

  const [rows, setRows] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<SavedReportsViewMode>(getInitialSavedReportsViewMode);
  const [deleteConfirmReportId, setDeleteConfirmReportId] = useState<string | null>(null);
  const [cardPage, setCardPage] = useState(1);
  const [addressSearchQuery, setAddressSearchQuery] = useState('');
  const [debouncedAddressSearchQuery, setDebouncedAddressSearchQuery] = useState('');

  useEffect(() => {
    setCardPage(1);
  }, [reports]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedAddressSearchQuery(addressSearchQuery);
    }, SAVED_REPORTS_ADDRESS_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [addressSearchQuery]);

  useEffect(() => {
    setCardPage(1);
  }, [debouncedAddressSearchQuery]);

  useEffect(() => {
    const sortedReports = reports
      ? [...reports].sort((a, b) => {
          // If either date is missing, treat it as lower priority (older)
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA;
        })
      : [];
    setRows(sortedReports);
  }, [reports]);

  useEffect(() => {
    try {
      localStorage.setItem(ESTIMATOR_SAVED_REPORTS_VIEW_KEY, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  const confirmGridDelete = () => {
    if (!deleteConfirmReportId) return;
    handleReport('DELETE', null, deleteConfirmReportId, false, true);
    setRows(prev => prev.filter(row => row.id !== deleteConfirmReportId));
    setDeleteConfirmReportId(null);
  };

  const filteredRows = useMemo(() => {
    const q = debouncedAddressSearchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => String(r.address ?? '').toLowerCase().includes(q));
  }, [rows, debouncedAddressSearchQuery]);

  useEffect(() => {
    setCardPage(p => {
      const maxPage = Math.max(1, Math.ceil(filteredRows.length / SAVED_REPORT_CARDS_PER_PAGE));
      return Math.min(Math.max(1, p), maxPage);
    });
  }, [filteredRows.length]);

  const paginatedCardRows = useMemo(
    () =>
      filteredRows.slice(
        (cardPage - 1) * SAVED_REPORT_CARDS_PER_PAGE,
        cardPage * SAVED_REPORT_CARDS_PER_PAGE
      ),
    [filteredRows, cardPage]
  );

  const savedReportCardPageCount = Math.max(
    1,
    Math.ceil(filteredRows.length / SAVED_REPORT_CARDS_PER_PAGE)
  );

  /**
   {
      "id": "8a36eb2451d185dcee90a00b",
      "report_type": "manage",
      "address": "Malibu",
      "url_path": "airbnb-calculator/malibu/8a36eb2451d185dcee90a00b",
      "ltm_revenue": 102889,
      "monthly_profit": 6859,
      "annual_return_rate": null,
      "created_at": "2025-10-29"
  }
   */

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: '#',
      width: 50,
      sortable: false,
      hide: true,
    },
    {
      field: 'actions',
      headerName: '',
      width: 40,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <IconButton
            color="primary"
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              setDeleteConfirmReportId(params.row?.['id'] ?? null);
            }}
          >
            <DeleteRoundedIcon />
          </IconButton>
        );
      },
    },
    {
      field: 'address',
      headerName: 'Address',
      width: 500,
      renderCell: (params: GridRenderCellParams) => (
        <Link
          component="button"
          type="button"
          underline="none"
          onClick={e => {
            e.stopPropagation();
            void handleReport('READ', null, params.row.id as string);
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            fontWeight: 'bold',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            font: 'inherit',
            color: 'inherit',
            textAlign: 'left',
            p: 0,
          }}
        >
          {params.row.address}
        </Link>
      ),
    },
    {
      field: 'report_type',
      headerName: 'Report Type',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const reportType = params.row.report_type;

        return <Box sx={{ textTransform: 'capitalize' }}>{reportType}</Box>;
      },
    },
    {
      field: 'created_at',
      headerName: 'Created On',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        return <Box sx={{ textTransform: 'capitalize' }}>{formatDate(params.row.created_at)}</Box>;
      },
    },
    {
      field: 'ltm_revenue',
      headerName: 'Revenue',
      width: 120,
      renderCell: (params: GridRenderCellParams) =>
        params.row.ltm_revenue
          ? exchangeRates[currency].symbol +
            formatCurrency(String(convertCurrency(currency, params.row.ltm_revenue as number)))
          : '_',
    },
    {
      field: 'monthly_profit',
      headerName: 'Monthly Profit',
      width: 120,
      renderCell: (params: GridRenderCellParams) =>
        params.row.monthly_profit
          ? exchangeRates[currency].symbol +
            formatCurrency(String(convertCurrency(currency, params.row.monthly_profit as number)))
          : '_',
    },
    {
      field: 'annual_return_rate',
      headerName: 'Annual Return Rate',
      width: 120,
      renderCell: (params: GridRenderCellParams) =>
        params.row.annual_return_rate ? params.row.annual_return_rate + '%' : '_',
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const rowTags: string[] = Array.isArray(params.row.tags) ? params.row.tags : [];
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
            {rowTags.slice(0, 4).map(t => (
              <Chip key={t} label={t} size="small" sx={{ maxWidth: 160 }} />
            ))}
            {rowTags.length > 4 ? (
              <Chip label={`+${rowTags.length - 4}`} size="small" variant="outlined" />
            ) : null}
          </Box>
        );
      },
    },
  ];

  return (
    <Grid
      container
      item
      xs
      sx={{
        width: '100%',
        backgroundColor: onlyRenderSavedReports ? '#fff' : '#E7F2F4',
        minHeight: '100px',
        borderTopLeftRadius: '40px',
        marginTop: onlyRenderSavedReports ? 0 : '-30px',
      }}
    >
      <Grid
        container
        item
        xs
        sx={{
          width: '100%',
          background: '#fff',
          marginTop: '20px',
          borderTopLeftRadius: '40px',
          padding: '20px',
        }}
      >
        {isLoading && <SavedCompsLoader />}

        {!isLoading && reports !== null && (
          <>
            <Grid container item xs={12} sx={{ mb: 2.5, width: '100%' }}>
              <Box
                sx={{
                  width: '100%',
                  border: '1px solid #e5e7eb',
                  borderRadius: '14px',
                  background: '#fafafa',
                  p: { xs: 1.5, sm: 1.75 },
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: matches ? 'column' : 'row',
                    alignItems: matches ? 'stretch' : 'center',
                    gap: matches ? 2 : 2,
                    width: '100%',
                  }}
                >
                  <Box
                    role="toolbar"
                    aria-label="Filter reports by tag"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      flexWrap: 'nowrap',
                      overflowX: 'auto',
                      flex: matches ? 'none' : '1 1 0%',
                      minWidth: matches ? '100%' : 0,
                      width: matches ? '100%' : 'auto',
                      pb: 0.25,
                      pt: 0.25,
                      px: 0.25,
                      '&::-webkit-scrollbar': { height: 6 },
                    }}
                  >
                    {tagFilterItems.map(item => {
                      const selected = selectedTagFilter === item.id;
                      return (
                        <Chip
                          key={item.id}
                          label={savedReportsTagChipLabel(item)}
                          size="small"
                          onClick={() => onTagFilterChange(item.id)}
                          color={selected ? 'primary' : 'default'}
                          variant={selected ? 'filled' : 'outlined'}
                          sx={{
                            flexShrink: 0,
                            borderRadius: '20px',
                            fontWeight: selected ? 600 : 500,
                            ...(selected
                              ? {}
                              : {
                                  borderColor: '#e5e7eb',
                                  backgroundColor: '#fff',
                                }),
                          }}
                          clickable
                          component="button"
                          type="button"
                          aria-pressed={selected}
                        />
                      );
                    })}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      flexWrap: 'wrap',
                      justifyContent: matches ? 'stretch' : 'flex-end',
                      flexShrink: 0,
                      width: matches ? '100%' : 'auto',
                      ...(matches
                        ? {
                            borderTop: '1px solid #e5e7eb',
                            pt: 2,
                            mt: 0.25,
                          }
                        : {}),
                    }}
                  >
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder="Search by address"
                      value={addressSearchQuery}
                      onChange={e => setAddressSearchQuery(e.target.value)}
                      sx={{
                        flexGrow: matches ? 1 : 0,
                        minWidth: matches ? 0 : 200,
                        maxWidth: matches ? '100%' : 320,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '999px',
                          backgroundColor: '#fff',
                          pl: 0.5,
                          '& fieldset': { borderColor: '#e5e7eb' },
                          '&:hover fieldset': { borderColor: '#d1d5db' },
                          '&.Mui-focused fieldset': {
                            borderColor: COLOR_PRIMARY_DARK,
                            borderWidth: '1px',
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ ml: 0.5 }}>
                            <SearchIcon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.85 }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <ToggleButtonGroup
                      value={viewMode}
                      exclusive
                      onChange={(_, next: SavedReportsViewMode | null) => next && setViewMode(next)}
                      size="small"
                      sx={{
                        borderRadius: '12px',
                        border: 'none',
                        backgroundColor: '#fff',
                        boxShadow: 'inset 0 0 0 1px #e5e7eb',
                        p: '3px',
                        gap: 0,
                        '& .MuiToggleButtonGroup-grouped': {
                          border: 0,
                          mx: 0,
                          '&:not(:first-of-type)': { borderLeft: 0, ml: 0 },
                        },
                        '& .MuiToggleButton-root': {
                          borderRadius: '9px !important',
                          px: 1.1,
                          py: 0.5,
                          border: 'none',
                          color: '#6b7280',
                        },
                        '& .MuiToggleButton-root.Mui-selected': {
                          backgroundColor: alpha(COLOR_PRIMARY, 0.18),
                          color: COLOR_PRIMARY_DARK,
                          boxShadow: '0 1px 2px rgba(3, 122, 144, 0.12)',
                          '&:hover': {
                            backgroundColor: alpha(COLOR_PRIMARY, 0.28),
                          },
                        },
                      }}
                    >
                      <Tooltip title="Card view">
                        <ToggleButton value="cards" aria-label="Card view">
                          <ViewModuleIcon fontSize="small" />
                        </ToggleButton>
                      </Tooltip>
                      <Tooltip title="Table view">
                        <ToggleButton value="table" aria-label="Table view">
                          <TableRowsIcon fontSize="small" />
                        </ToggleButton>
                      </Tooltip>
                    </ToggleButtonGroup>
                    {viewMode === 'cards' && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FileDownloadOutlinedIcon />}
                        onClick={() => downloadSavedReportsCsv(filteredRows, currency)}
                        disabled={filteredRows.length === 0}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: '10px',
                          borderColor: '#e5e7eb',
                          color: '#374151',
                          '&:hover': { borderColor: COLOR_PRIMARY_DARK, background: '#f0fdfa' },
                        }}
                      >
                        Export
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>

            {viewMode === 'cards' ? (
              <Grid container item xs={12} spacing={3}>
                {rows.length === 0 ? (
                  <Grid item xs={12}>
                    <Typography variant="body1" color="text.secondary" sx={{ py: 2 }}>
                      {isTagFilterActive
                        ? 'No reports match this tag filter.'
                        : 'No saved reports yet.'}
                    </Typography>
                  </Grid>
                ) : filteredRows.length === 0 ? (
                  <Grid item xs={12}>
                    <Typography variant="body1" color="text.secondary" sx={{ py: 2 }}>
                      No reports match your search.
                    </Typography>
                  </Grid>
                ) : (
                  paginatedCardRows.map(report => (
                    <Grid item xs={12} sm={6} md={4} key={report.id}>
                      <SavedReportCard
                        report={report}
                        currency={currency}
                        handleReport={handleReport}
                        setComingSoonModalOpen={setComingSoonModalOpen}
                        onlyRenderSavedReports={onlyRenderSavedReports}
                        ota={ota}
                        onTagsChanged={refreshSavedReports}
                        existingUserTagNames={existingUserTagNames}
                      />
                    </Grid>
                  ))
                )}
              </Grid>
            ) : null}

            {viewMode === 'cards' && filteredRows.length > SAVED_REPORT_CARDS_PER_PAGE ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, width: '100%' }}>
                <Pagination
                  count={savedReportCardPageCount}
                  page={cardPage}
                  onChange={(_, p) => setCardPage(p)}
                  color="primary"
                  size="small"
                />
              </Box>
            ) : null}

            {viewMode === 'table' ? (
              <Grid container item xs={12} spacing={4} sx={{ height: '100%' }}>
                <Grid item xs={12}>
                  <Paper style={{ width: '100%', position: 'relative' }}>
                    <DataGrid
                      rows={filteredRows}
                      columns={columns}
                      autoHeight
                      components={{ Toolbar: CustomToolbar }}
                      onRowClick={(params, event) => {
                        const el = event.target as HTMLElement;
                        if (el.closest('button')) return;
                        void handleReport('READ', null, String(params.id));
                      }}
                    />
                  </Paper>
                </Grid>
              </Grid>
            ) : null}

            <Dialog
              open={deleteConfirmReportId !== null}
              onClose={() => setDeleteConfirmReportId(null)}
            >
              <DialogTitle id="alert-dialog-title-saved-reports-grid-delete">
                Are you sure you want to delete this report?
              </DialogTitle>
              <DialogActions sx={{ padding: '20px' }}>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<CloseRoundedIcon />}
                  onClick={() => setDeleteConfirmReportId(null)}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteOutlineRoundedIcon />}
                  onClick={confirmGridDelete}
                >
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Grid>
    </Grid>
  );
};

const SavedCompsLoader = () => {
  return (
    <Box
      sx={{
        width: '100%',
        height: '200px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <CircularProgress size={40} />
    </Box>
  );
};

const SearchPage = ({
  user,
  matches,
  currency,
  dummyMarket,
  googleAutocompleteSessionToken,
  getGoogleAutocompleteSessionToken,
  clearGoogleAutocompleteSessionToken,
  googleAutocompleteSessionTokenTimeStamp,
  handleReport,
  handleLoginOrPricingModalOpen,
  setComingSoonModalOpen,
  savedReports,
  setSavedReports,
  resetCurrencyToDefault,
  searchbarOnly,
  ota = 'airbnb',
  isApiRateLimited,
  setIsApiRateLimited,
}: {
  user: User;
  matches: boolean;
  currency: string;
  dummyMarket: any;
  googleAutocompleteSessionToken: any;
  getGoogleAutocompleteSessionToken: any;
  clearGoogleAutocompleteSessionToken: any;
  googleAutocompleteSessionTokenTimeStamp: any;
  handleReport: (method: any, requestBody: null | any, id?: string) => Promise<{ success: boolean }>;
  handleLoginOrPricingModalOpen: handleLoginOrPricingModalOpenType;
  setComingSoonModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  savedReports: {
    data: any[] | null;
    tag_summary: { name: string; count: number }[];
    untagged_count: number;
    loading: boolean;
  };
  setSavedReports: React.Dispatch<
    React.SetStateAction<{
      data: any[] | null;
      tag_summary: { name: string; count: number }[];
      untagged_count: number;
      loading: boolean;
    }>
  >;
  resetCurrencyToDefault: () => void;
  searchbarOnly: boolean;
  ota?: 'airbnb' | 'booking';
  isApiRateLimited: boolean;
  setIsApiRateLimited: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const onlyRenderSavedReports = checkIfSettingPage();

  const [userReportCreationCount, setUserReportCreationCount] = useState<number>(0);
  const [selectedTagFilter, setSelectedTagFilter] = useState<string>(TAG_FILTER_ALL);

  const tagFilterItems: EstimatorReportTagFilterItem[] = useMemo(() => {
    const summary = savedReports.tag_summary ?? [];
    const untaggedCount = savedReports.untagged_count ?? 0;
    return [
      { id: TAG_FILTER_ALL, name: 'All reports', isDefault: true },
      { id: TAG_FILTER_UNTAGGED, name: 'Untagged', count: untaggedCount },
      ...summary.map(t => ({ id: t.name, name: t.name, count: t.count })),
    ];
  }, [savedReports.tag_summary, savedReports.untagged_count]);

  const existingUserTagNames = useMemo(
    () => (savedReports.tag_summary ?? []).map(t => t.name),
    [savedReports.tag_summary]
  );

  const fetchSavedReports = useCallback(async () => {
    if (searchbarOnly) return;
    setSavedReports(prev => ({ ...prev, loading: true }));
    const queryParams: Record<string, string> = { ota, apiType: 'v2' };
    if (selectedTagFilter === TAG_FILTER_UNTAGGED) {
      queryParams.untagged = '1';
    } else if (selectedTagFilter !== TAG_FILTER_ALL) {
      queryParams.tag = encodeURIComponent(selectedTagFilter);
    }
    const data = await callApi(
      CALCULATOR_API_URL,
      { queryParams },
      true,
      false,
      '',
      'GET',
      true,
      undefined,
      true,
      true
    );
    if (data && data.status == 429) {
      setIsApiRateLimited(true);
      setSavedReports(prev => ({ ...prev, loading: false }));
      return;
    }
    if (!data || data === 'Unauthenticated request') {
      setSavedReports({
        data: null,
        tag_summary: [],
        untagged_count: 0,
        loading: false,
      });
    } else {
      setSavedReports({
        data: data.reports,
        tag_summary: data.tag_summary ?? [],
        untagged_count: data.untagged_count ?? 0,
        loading: false,
      });
      setUserReportCreationCount(data.count ?? 0);
    }
  }, [searchbarOnly, ota, selectedTagFilter, setIsApiRateLimited, setSavedReports]);

  const refreshSavedReports = useCallback(async () => {
    await fetchSavedReports();
  }, [fetchSavedReports]);

  useEffect(() => {
    logAmplitudeEvent('report_landing_page_visited');
    resetCurrencyToDefault();
  }, []);

  useEffect(() => {
    if (searchbarOnly) return;
    fetchSavedReports();
  }, [searchbarOnly, fetchSavedReports]);

  return (
    <>
      {isApiRateLimited && (
        <Grid container item xs={12}>
          <RateLimitAlert />
        </Grid>
      )}
      {ota === 'booking' && (
        <Grid container sx={styles.root(matches)} item xs={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              marginBottom: '-60px',
              marginTop: '60px',
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontWeight: 500, textAlign: 'center', display: 'flex', alignItems: 'center' }}
            >
              Instantly See Your Estimated Revenue from{' '}
              <span style={{ color: COLOR_PRIMARY_2, paddingLeft: '10px' }}>Booking.com</span>
            </Typography>
          </Box>
        </Grid>
      )}

      {!onlyRenderSavedReports && (
        <Grid container sx={styles.root(matches)} item xs={12}>
          <SearchBar
            user={user}
            matches={matches}
            dummyMarket={dummyMarket}
            googleAutocompleteSessionToken={googleAutocompleteSessionToken}
            getGoogleAutocompleteSessionToken={getGoogleAutocompleteSessionToken}
            clearGoogleAutocompleteSessionToken={clearGoogleAutocompleteSessionToken}
            googleAutocompleteSessionTokenTimeStamp={googleAutocompleteSessionTokenTimeStamp}
            handleReport={handleReport}
            handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
            userReportCreationCount={userReportCreationCount}
            searchbarOnly={searchbarOnly}
            ota={ota}
          />
        </Grid>
      )}

      {!searchbarOnly && (
        <SavedReports
          isLoading={savedReports.loading}
          reports={savedReports.data}
          currency={currency}
          handleReport={handleReport}
          setComingSoonModalOpen={setComingSoonModalOpen}
          matches={matches}
          ota={ota}
          refreshSavedReports={refreshSavedReports}
          isTagFilterActive={selectedTagFilter !== TAG_FILTER_ALL}
          tagFilterItems={tagFilterItems}
          selectedTagFilter={selectedTagFilter}
          onTagFilterChange={setSelectedTagFilter}
          existingUserTagNames={existingUserTagNames}
        />
      )}
    </>
  );
};

export default SearchPage;
